/**
 * The engine: fixed-step simulation with an interpolated render pass.
 * ARCHITECTURE.md §1. This file implements the authoritative frame order.
 */

import { FIXED_STEP, MAX_STEPS_PER_FRAME } from './constants.js';
import { nowMs } from './time.js';

export class Engine {
  /** @param {object} ctx from createContext() */
  constructor(ctx) {
    this.ctx = ctx;
    this.running = false;
    this.initialised = false;
    this._rafId = 0;
    this._boundFrame = (t) => this._frame(t);
    /** Systems that implement each hook, precomputed to avoid per-frame checks. */
    this._fixed = [];
    this._update = [];
    this._late = [];
    this._resize = [];
    /** Parallel arrays of channel objects so per-frame timing avoids Map lookups. */
    this._fixedCh = [];
    this._updateCh = [];
    this._lateCh = [];
    this._frameStart = 0;
    /** Frame-step mode: run exactly N steps then pause. Used by training mode. */
    this._stepRequest = 0;
    this.onError = null;
  }

  register(...SystemClasses) {
    for (const S of SystemClasses) {
      if (Array.isArray(S)) this.register(...S);
      else if (S) this.ctx.systems.add(S);
    }
    return this;
  }

  async init() {
    if (this.initialised) throw new Error('Engine.init() called twice.');
    const ctx = this.ctx;
    const ordered = ctx.systems.build();
    ctx.log.info(`system order: ${ordered.map((s) => s.constructor.id).join(' -> ')}`);

    for (const sys of ordered) {
      const id = sys.constructor.id;
      const t0 = nowMs();
      try {
        await sys.init(ctx);
      } catch (err) {
        ctx.log.error(`system "${id}" failed to init`, err);
        throw err;
      }
      ctx.metrics.sample(`init.${id}`, nowMs() - t0);
    }

    // Precompute hook lists. A system that does not override a hook is skipped entirely.
    const proto = Object.getPrototypeOf;
    for (const sys of ordered) {
      const id = sys.constructor.id;
      const budget = sys.constructor.budgetMs || 0;
      if (hasOwnHook(sys, 'fixedUpdate')) {
        this._fixed.push(sys);
        this._fixedCh.push(ctx.metrics.channel(`sys.${id}.fixed`, budget));
      }
      if (hasOwnHook(sys, 'update')) {
        this._update.push(sys);
        this._updateCh.push(ctx.metrics.channel(`sys.${id}.update`, budget));
      }
      if (hasOwnHook(sys, 'lateUpdate')) {
        this._late.push(sys);
        this._lateCh.push(ctx.metrics.channel(`sys.${id}.late`, budget));
      }
      if (hasOwnHook(sys, 'resize')) this._resize.push(sys);
      void proto;
    }

    this.initialised = true;
    return this;
  }

  start() {
    if (!this.initialised) throw new Error('Engine.start() before init().');
    if (this.running) return this;
    if (this.ctx.flags.deterministic) {
      this.ctx.log.warn('Engine.start() ignored: context is deterministic; use runDeterministic().');
      return this;
    }
    this.running = true;
    this._rafId = requestAnimationFrame(this._boundFrame);
    return this;
  }

  stop() {
    this.running = false;
    if (this._rafId) cancelAnimationFrame(this._rafId);
    this._rafId = 0;
    return this;
  }

  /** Requests exactly `n` simulation steps while paused. Training-mode frame stepping. */
  requestSteps(n = 1) {
    this._stepRequest += n;
  }

  _frame(rawNow) {
    if (!this.running) return;
    this._rafId = requestAnimationFrame(this._boundFrame);
    const ctx = this.ctx;
    const t = ctx.time;
    const frameT0 = nowMs();
    this._frameStart = frameT0;

    let steps = t.beginFrame(rawNow);
    if (this._stepRequest > 0) {
      // Frame stepping overrides accumulated time so a paused clock can still advance.
      steps = Math.min(this._stepRequest, MAX_STEPS_PER_FRAME);
      this._stepRequest -= steps;
    }

    const fixedT0 = nowMs();
    for (let s = 0; s < steps; s++) this._runFixedStep();
    ctx.metrics.fixedChannel.push(nowMs() - fixedT0);
    t.endSteps();

    const dt = t.frameDt;
    const upT0 = nowMs();
    this._runHooks(this._update, this._updateCh, 'update', dt, ctx);
    ctx.metrics.updateChannel.push(nowMs() - upT0);

    this._runHooks(this._late, this._lateCh, 'lateUpdate', dt, ctx);

    const frameMs = nowMs() - frameT0;
    ctx.metrics.frameChannel.push(frameMs);
    if (frameMs > 50) this._attributeLongFrame(frameMs);
    t.endFrame();
  }

  _runFixedStep() {
    const ctx = this.ctx;
    ctx.time.advanceTick();
    const systems = this._fixed;
    const chans = this._fixedCh;
    for (let i = 0; i < systems.length; i++) {
      const t0 = nowMs();
      try {
        systems[i].fixedUpdate(FIXED_STEP, ctx);
      } catch (err) {
        this._systemError(systems[i], 'fixedUpdate', err);
      }
      chans[i].push(nowMs() - t0);
    }
    ctx.events.flushDeferred();
  }

  _runHooks(systems, chans, hook, dt, ctx) {
    for (let i = 0; i < systems.length; i++) {
      const t0 = nowMs();
      try {
        systems[i][hook](dt, ctx);
      } catch (err) {
        this._systemError(systems[i], hook, err);
      }
      chans[i].push(nowMs() - t0);
    }
  }

  _systemError(sys, hook, err) {
    const id = sys.constructor.id;
    this.ctx.metrics.counter(`error.${id}.${hook}`);
    if (this.ctx.flags.deterministic) throw err;
    this.ctx.log.error(`system "${id}".${hook} threw`, err);
    if (this.onError) this.onError(id, hook, err);
  }

  _attributeLongFrame(frameMs) {
    let worst = '';
    let worstMs = 0;
    const scan = (systems, chans, suffix) => {
      for (let i = 0; i < systems.length; i++) {
        const ms = chans[i].last;
        if (ms > worstMs) {
          worstMs = ms;
          worst = `${systems[i].constructor.id}.${suffix}`;
        }
      }
    };
    scan(this._fixed, this._fixedCh, 'fixed');
    scan(this._update, this._updateCh, 'update');
    scan(this._late, this._lateCh, 'late');
    this.ctx.metrics.noteLongFrame(this.ctx.time.tick, frameMs, worst, worstMs);
  }

  /**
   * Advances exactly `ticks` fixed steps with zero wall-clock involvement.
   * The only stepping path used by deterministic tools and tests.
   * @param {number} ticks
   * @param {boolean} [present] run one update/lateUpdate pair afterwards (for a capture)
   */
  runDeterministic(ticks, present = false) {
    if (!this.initialised) throw new Error('Engine.runDeterministic() before init().');
    const ctx = this.ctx;
    const wasDeterministic = ctx.time.deterministic;
    ctx.time.deterministic = true;
    for (let i = 0; i < ticks; i++) this._runFixedStep();
    ctx.time.endSteps();
    if (present) {
      this._runHooks(this._update, this._updateCh, 'update', FIXED_STEP, ctx);
      this._runHooks(this._late, this._lateCh, 'lateUpdate', FIXED_STEP, ctx);
      ctx.time.endFrame();
    }
    ctx.time.deterministic = wasDeterministic;
    return ctx.time.tick;
  }

  /** Presents one frame without advancing the simulation. Used between captures. */
  present() {
    const ctx = this.ctx;
    this._runHooks(this._update, this._updateCh, 'update', 0, ctx);
    this._runHooks(this._late, this._lateCh, 'lateUpdate', 0, ctx);
    ctx.time.endFrame();
  }

  resize(width, height, dpr) {
    const ctx = this.ctx;
    ctx.render.width = width;
    ctx.render.height = height;
    if (dpr !== undefined) ctx.render.dpr = dpr;
    for (const sys of this._resize) {
      try {
        sys.resize(width, height, ctx);
      } catch (err) {
        this._systemError(sys, 'resize', err);
      }
    }
  }

  async dispose() {
    this.stop();
    const ordered = this.ctx.systems.ordered;
    for (let i = ordered.length - 1; i >= 0; i--) {
      const sys = ordered[i];
      try {
        await sys.dispose();
      } catch (err) {
        this.ctx.log.error(`system "${sys.constructor.id}".dispose threw`, err);
      }
    }
    this.ctx.events.clear();
    this.ctx.pools.releaseAll();
    this.ctx.systems.clear();
    this._fixed.length = 0;
    this._update.length = 0;
    this._late.length = 0;
    this._resize.length = 0;
    this._fixedCh.length = 0;
    this._updateCh.length = 0;
    this._lateCh.length = 0;
    this.initialised = false;
  }
}

/**
 * True when the instance actually implements the hook, rather than inheriting the
 * empty base-class version. Keeps the per-frame hook lists tight.
 */
function hasOwnHook(instance, name) {
  let proto = Object.getPrototypeOf(instance);
  while (proto && proto.constructor !== Object) {
    if (Object.prototype.hasOwnProperty.call(proto, name)) {
      // Reject the no-op System base implementation.
      if (proto.constructor && proto.constructor.name === 'System') return false;
      return typeof proto[name] === 'function';
    }
    proto = Object.getPrototypeOf(proto);
  }
  return false;
}
